import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import ShareVideoButton from "./ShareVideoButton";
import { useVideoWatermark } from "@/hooks/useVideoWatermark";

interface VideoWatermarkGeneratorProps {
  videoFile: File;
}

export default function VideoWatermarkGenerator({
  videoFile,
}: VideoWatermarkGeneratorProps) {
  const { t } = useTranslation();
  const [watermarkLogo, setWatermarkLogo] = useState<File | null>(null);
  const [showProcessing, setShowProcessing] = useState(false);

  // Load watermark logo automatically
  useEffect(() => {
    const loadWatermark = async () => {
      try {
        const response = await fetch("/logo-watermark.png");

        if (!response.ok) {
          console.error("Failed to load watermark:", response.status);
          return;
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.startsWith("image/")) {
          console.error("Watermark file is not an image");
          return;
        }

        const blob = await response.blob();
        const file = new File([blob], "watermark.png", { type: "image/png" });
        setWatermarkLogo(file);
        console.log("Watermark loaded successfully:", file.size, "bytes");
      } catch (err) {
        console.error("Failed to load watermark:", err);
      }
    };

    loadWatermark();
  }, []);

  const {
    isLoading: isFFmpegLoading,
    isProcessing,
    progress,
    watermarkedVideoUrl,
    watermarkedVideoFile,
    error,
    processVideo,
  } = useVideoWatermark({
    videoFile,
    watermarkImage: watermarkLogo,
    onComplete: (url) => {
      console.log("Watermark complete:", url);
    },
    onError: (error) => {
      console.error("Watermark error:", error);
      alert(t("conclusion.error") || "Error processing video. Please try again.");
    },
  });

  const handleGenerateVideo = async () => {
    setShowProcessing(true);
    await processVideo();
  };

  // Don't render anything if watermark hasn't loaded yet
  if (!watermarkLogo || isFFmpegLoading) {
    return null;
  }

  // Show initial prompt and button
  if (!showProcessing) {
    return (
      <div className="w-full space-y-4">
        <p className="text-sm text-white">
          {t("conclusion.share-prompt") || "Want to share your review on social media?"}
        </p>
        <Button onPress={handleGenerateVideo} variant="tertiary">
          {t("conclusion.prepare-video") || "Prepare video for sharing"}
        </Button>
      </div>
    );
  }

  // Show processing state
  if (isProcessing) {
    return (
      <div className="w-full">
        <div className="space-y-4">
          <div className="text-center">
            <p className="mb-2 text-lg font-semibold text-white">
              {t("conclusion.processing") || "Processing video..."}
            </p>
            <div className="mb-2 h-4 w-full rounded-full bg-gray-700">
              <div
                className="h-4 rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-300">{progress}%</p>
            <p className="mt-2 text-xs text-gray-400">
              {t("conclusion.auto-share-note") ||
                "We're processing your video. This may take a few minutes."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full">
        <div className="mb-4 text-red-400">
          <p>{t("conclusion.error") || "An error occurred during processing."}</p>
          <Button onPress={handleGenerateVideo} className="mt-2">
            {t("conclusion.retry") || "Retry"}
          </Button>
        </div>
      </div>
    );
  }

  // Show success state with share button
  if (watermarkedVideoFile && watermarkedVideoUrl) {
    return (
      <div className="w-full">
        <div className="space-y-4 text-center">
          <p className="mb-2 font-semibold text-green-400">
            {t("conclusion.ready") || "✓ Video ready for sharing!"}
          </p>
          <ShareVideoButton videoFile={watermarkedVideoFile} />
        </div>
      </div>
    );
  }

  return null;
}
